import React, { useEffect, useMemo, useState } from 'react';
import { Card, Typography, Spin, Alert } from 'antd';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import type { RootState } from '@/store';
import {
  getDashboardMetric3,
  type DashboardMetric3ProcessControlCount,
} from '@/services/dashboardService';
import { EffectivenessStatus } from '@/services/controlActivitiesService';

const { Text } = Typography;

// Required colors (match spec)
const EFFECTIVE_COLOR = '#22C55E'; // green
const INEFFECTIVE_COLOR = '#EF4444'; // red
const UNTESTED_COLOR = '#E6A700'; // yellow/gold
const OUTOFSCOPE_COLOR = '#9CA3AF'; // grey

type Datum = {
  processId: string;
  processLabel: string; // "A01 Production Accounting"
  processCode: string;
  processName: string;
  effective: number;
  ineffective: number;
  untested: number;
  outofscope: number;
  total: number;
};

export type EX1ControlDistributionByProcessGroupedHorizontalBarChartProps = {
  height?: number;
  title?: string;
};

const Metric3Tooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;

  // payload includes 3 entries (effective/ineffective/untested)
  const items = payload
    .filter((p: any) => typeof p?.value === 'number')
    .map((p: any) => ({ name: p.name, value: p.value, color: p.color }));

  const total = items.reduce((s: number, x: any) => s + (x.value ?? 0), 0);

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: 8,
        padding: 10,
        minWidth: 240,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{label}</div>

      {items.map((x: any) => (
        <div
          key={x.name}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginTop: 4,
          }}
        >
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 2,
              background: x.color,
              display: 'inline-block',
            }}
          />
          <span style={{ flex: 1 }}>{x.name}</span>
          <span style={{ fontWeight: 600 }}>{x.value}</span>
        </div>
      ))}

      <div
        style={{
          marginTop: 8,
          paddingTop: 8,
          borderTop: '1px solid rgba(0,0,0,0.08)',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <span>Total</span>
        <span style={{ fontWeight: 700 }}>{total}</span>
      </div>

      <div style={{ marginTop: 8, color: 'rgba(0,0,0,0.6)' }}>
        Click a bar to open Exhibit 5 filtered by Process + Status
      </div>
    </div>
  );
};

export const EX1ControlDistributionByProcessGroupedHorizontalBarChart: React.FC<
  EX1ControlDistributionByProcessGroupedHorizontalBarChartProps
> = ({ height = 380, title = 'Control Distribution by Process' }) => {
  const navigate = useNavigate();

  // annualReportId from Redux
  const { selectedReport } = useSelector(
    (state: RootState) => state.annualReport,
  );
  const annualReportId = selectedReport?.annualReportId;

  const [raw, setRaw] = useState<DashboardMetric3ProcessControlCount[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      if (!annualReportId) {
        setRaw([]);
        setLoading(false);
        setErrorMessage(null);
        return;
      }

      setLoading(true);
      setErrorMessage(null);

      try {
        const data = await getDashboardMetric3(annualReportId);
        if (!isMounted) return;
        setRaw(Array.isArray(data) ? data : []);
      } catch (err: any) {
        if (!isMounted) return;
        setRaw([]);
        setErrorMessage(
          err?.message ||
            err?.Message ||
            'Failed to load Metric 3 (Controls count breakdown by process).',
        );
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [annualReportId]);

  const data: Datum[] = useMemo(() => {
    return (
      raw
        .map(p => {
          const label = `${p.processCode} ${p.processName}`.trim();
          const effective = p.effectiveControls ?? 0;
          const ineffective = p.ineffectiveControls ?? 0;
          const untested = p.untestedControls ?? 0;
          const outofscope = p.outOfScopeControls ?? 0;

          return {
            processId: p.processId,
            processLabel: label,
            processCode: p.processCode,
            processName: p.processName,
            effective,
            ineffective,
            untested,
            outofscope,
            total: effective + ineffective + untested + outofscope,
          };
        })
    );
  }, [raw]);

  const statusTotals = useMemo(() => {
    return data.reduce(
      (acc, d) => {
        acc.effective += d.effective ?? 0;
        acc.ineffective += d.ineffective ?? 0;
        acc.untested += d.untested ?? 0;
        acc.outofscope += d.outofscope ?? 0;
        return acc;
      },
      {
        effective: 0,
        ineffective: 0,
        untested: 0,
        outofscope: 0
      },
    );
  }, [data]);

  const isEmpty = !loading && !errorMessage && data.length === 0;

  // Routing (adjust to match Exhibit 5 expectations)
  const goToExhibit5 = (
    processId: string,
    processCode: string,
    processName: string,
    status: EffectivenessStatus,
  ) => {
    navigate('/control-activities', {
      state: {
        processId: processId,
        processCode: processCode,
        processName: processName,
        effectivenessStatus: status,
      },
    });
  };

  const computedHeight = useMemo(() => {
    const rowHeight = 55; // tweak: 24–36 depending on font
    const min = height; // use your prop as minimum
    const needed = data.length * rowHeight + 80; // padding for axes/legend
    return Math.max(min, needed);
  }, [data.length, height]);

  const maxTotal = useMemo(() => {
    return Math.max(0, ...data.map(d => d.effective ?? 0), ...data.map(d => d.ineffective ?? 0), ...data.map(d => d.untested ?? 0), ...data.map(d => d.outofscope ?? 0));
  }, [data]);

  const xTicks = useMemo(() => {
    // 1..maxTotal (same behavior as your other chart)
    return Array.from({ length: maxTotal }, (_, i) => i + 1);
  }, [maxTotal]);

  return (
    <Card title={title} extra={<Text type='secondary'></Text>}>
      <div style={{ height: computedHeight }}>
        {loading ? (
          <div
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Spin />
          </div>
        ) : errorMessage ? (
          <Alert
            type='error'
            message='Dashboard Metric 3 Error'
            description={errorMessage}
            showIcon
          />
        ) : isEmpty ? (
          <Alert
            type='info'
            message='No data'
            description='No processes were returned.'
            showIcon
          />
        ) : (
          <ResponsiveContainer width='100%' height='100%'>
            <BarChart
              data={data}
              layout='vertical'
              margin={{ top: 8, right: 140, bottom: 24, left: 32 }}
              barCategoryGap={14} // spacing between process groups
              barGap={4} // spacing within group (3 bars)
            >
              <XAxis
                type='number'
                domain={[0, maxTotal]}
                ticks={xTicks}
                allowDecimals={false}
                label={{
                  value: 'Count of Controls',
                  position: 'insideBottom',
                  offset: -10,
                }}
              />

              <YAxis
                type='category'
                dataKey='processLabel'
                width={190}
                interval={0}
                tickMargin={8}
                label={{
                  value: 'Process Name',
                  angle: -90,
                  position: 'insideLeft',
                  offset: -25,
                }}
              />

              <Tooltip content={<Metric3Tooltip />} />
              <Legend
                layout='vertical'
                align='right'
                verticalAlign='middle'
                wrapperStyle={{ paddingLeft: 16 }}
                formatter={(value: string) => {
                  switch (value) {
                    case 'Effective':
                      return `Effective (${statusTotals.effective})`;
                    case 'Ineffective':
                      return `Ineffective (${statusTotals.ineffective})`;
                    case 'Untested':
                      return `Untested (${statusTotals.untested})`;
                    case 'Out Of Scope':
                      return `Out Of Scope (${statusTotals.outofscope})`;
                    default:
                      return value;
                  }
                }}
              />

              <Bar
                dataKey='effective'
                name='Effective'
                fill={EFFECTIVE_COLOR}
                isAnimationActive={false}
                onClick={(barData: any) => {
                  const processId = barData?.payload?.processId;
                  const processCode = barData?.payload?.processCode;
                  const processName = barData?.payload?.processName;
                  if (processId) goToExhibit5(processId, processCode, processName, 'effective');
                }}
                style={{ cursor: 'pointer' }}
              />

              <Bar
                dataKey='ineffective'
                name='Ineffective'
                fill={INEFFECTIVE_COLOR}
                isAnimationActive={false}
                onClick={(barData: any) => {
                  const processId = barData?.payload?.processId;
                  const processCode = barData?.payload?.processCode;
                  const processName = barData?.payload?.processName;
                  if (processId) goToExhibit5(processId, processCode, processName, 'ineffective');
                }}
                style={{ cursor: 'pointer' }}
              />

              <Bar
                dataKey='untested'
                name='Untested'
                fill={UNTESTED_COLOR}
                isAnimationActive={false}
                onClick={(barData: any) => {
                  const processId = barData?.payload?.processId;
                  const processCode = barData?.payload?.processCode;
                  const processName = barData?.payload?.processName;
                  if (processId) goToExhibit5(processId, processCode, processName, 'untested');
                }}
                style={{ cursor: 'pointer' }}
              />

              <Bar
                dataKey='outofscope'
                name='Out Of Scope'
                fill={OUTOFSCOPE_COLOR}
                isAnimationActive={false}
                onClick={(barData: any) => {
                  const processId = barData?.payload?.processId;
                  const processCode = barData?.payload?.processCode;
                  const processName = barData?.payload?.processName;
                  if (processId) goToExhibit5(processId, processCode, processName, 'outofscope');
                }}
                style={{ cursor: 'pointer' }}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
};

export default EX1ControlDistributionByProcessGroupedHorizontalBarChart;
